import { compact, omit } from "lodash";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import {
  AssessmentGroupProviders,
  AssessmentType,
  ComplianceProgramRecurrence,
  ComplianceProgramStep,
  CreateProgramScheduledReminderMutationMutationVariables,
  ProgramScheduledReminderInput,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { TableBodyCell } from "@components/table/table";
import { TableBodyRow } from "@components/table/table";
import { TableBody } from "@components/table/table";
import { Table, TableHeadRow } from "@components/table/table";
import { TableHeadCell } from "@components/table/table";
import { TableContainer } from "@components/table/table";
import { Tooltip } from "@components/tooltip/tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { getAssessmentTypeLabel } from "@helpers/constants";
import useConfirm from "@helpers/hooks/use-confirm";

import ComplianceProgramReminderDialog from "../compliance-program-reminder-dialog";
import { ProgramForm } from "./types";

const ComplianceProgramFormReminders = ({
  proposedComplianceProgram,
  onUpdateProposedComplianceProgram,
  canUpdate,
}: {
  proposedComplianceProgram: ProgramForm;
  onUpdateProposedComplianceProgram: (data: Partial<ProgramForm>) => void;
  canUpdate: boolean;
}) => {
  const label = useLabel();
  const [isShowingSendReminderDialog, setIsShowingSendReminderDialog] =
    useState(false);

  const activeSteps = proposedComplianceProgram.steps.map(
    ({ stepType }) => stepType,
  );

  const scheduledReminders = useMemo(
    () =>
      (proposedComplianceProgram.scheduledReminders ||
        []) as ProgramScheduledReminderInput[],
    [proposedComplianceProgram.scheduledReminders],
  );

  const handleClickCreateReminder = useCallback(
    (
      variables: Omit<
        CreateProgramScheduledReminderMutationMutationVariables,
        "complianceProgramId"
      >,
    ) => {
      onUpdateProposedComplianceProgram({
        scheduledReminders: [
          ...scheduledReminders,
          omit(variables, "sendNow", "complianceProgramId"),
        ],
      });
      setIsShowingSendReminderDialog(false);
    },
    [onUpdateProposedComplianceProgram, scheduledReminders],
  );

  const { confirm, ConfirmationDialog } = useConfirm(
    "Delete reminder?",
    "Are you sure you want to delete this reminder?",
  );

  const handleDeleteReminder = useCallback(
    async (indexToRemove: number, idToRemove?: number | null) => {
      const confirmed = await confirm();
      if (confirmed) {
        onUpdateProposedComplianceProgram({
          scheduledReminders: scheduledReminders.filter((reminder, index) => {
            if (idToRemove) {
              return reminder.id !== idToRemove;
            }
            return index !== indexToRemove;
          }),
        });
      }
    },
    [onUpdateProposedComplianceProgram, scheduledReminders, confirm],
  );

  const peerAssessmentStep = proposedComplianceProgram.steps.find(
    (step) => step.stepType === ComplianceProgramStep.PeerReview,
  );

  const cannotCreateReminder = useMemo(() => {
    if (
      !proposedComplianceProgram.startDate &&
      !proposedComplianceProgram.ongoing
    ) {
      return "Must have a start date";
    }
    if (proposedComplianceProgram.steps.length === 0) {
      return "Must have at least one step";
    }
    return null;
  }, [
    proposedComplianceProgram.startDate,
    proposedComplianceProgram.ongoing,
    proposedComplianceProgram.steps,
  ]);

  const programRecurrence =
    proposedComplianceProgram.recurrence ?? ComplianceProgramRecurrence.None;
  const programOngoing = proposedComplianceProgram.ongoing ?? false;
  const showRelativeDateInput =
    programRecurrence !== ComplianceProgramRecurrence.None || programOngoing;

  return (
    <>
      <ConfirmationDialog />
      {isShowingSendReminderDialog && (
        <ComplianceProgramReminderDialog
          complianceProgramStartDate={moment(
            proposedComplianceProgram.startDate,
          ).toDate()}
          complianceProgramRecurrence={programRecurrence}
          showRelativeDateInput={showRelativeDateInput} // always show in dialog
          complianceProgramOngoing={programOngoing}
          onClose={() => setIsShowingSendReminderDialog(false)}
          cols={compact([
            activeSteps.includes(ComplianceProgramStep.SelfReview) && {
              label: `${getAssessmentTypeLabel(
                AssessmentType.Performance,
                true,
              )} ${label("review", { titleCase: true })}`,
              keyName: "performanceSelfAssessmentStatus",
            },
            activeSteps.includes(ComplianceProgramStep.DownwardReview) && {
              label: `${getAssessmentTypeLabel(
                AssessmentType.Performance,
              )} ${label("review", { titleCase: true })}`,
              keyName: "performanceAssessmentStatus",
            },
            activeSteps.includes(ComplianceProgramStep.UpwardReview) && {
              label: `${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
                "review",
                { titleCase: true },
              )}`,
              keyName: "managerAssessmentStatus",
            },
            activeSteps.includes(ComplianceProgramStep.PeerReview) &&
              peerAssessmentStep &&
              peerAssessmentStep?.providers !==
                AssessmentGroupProviders.Default && {
                label: `${getAssessmentTypeLabel(
                  AssessmentType.Peer,
                )} Nominations`,
                keyName: "nominations",
              },
            activeSteps.includes(ComplianceProgramStep.PeerReview) && {
              label: `${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
                "review",
                { titleCase: true },
              )}`,
              keyName: "peerAssessmentStatus",
            },
            activeSteps.includes(ComplianceProgramStep.OneOnOne) && {
              label: `${label("oneonone", { titleCase: true })} Meetings`,
              keyName: "oneononeStatus",
            },
            activeSteps.includes(ComplianceProgramStep.EngagementSurvey) && {
              label: `${getAssessmentTypeLabel(
                AssessmentType.EngagementSurvey,
              )}`,
              keyName: "engagementSurveyStatus",
            },
          ])}
          selectedTeam={null}
          onSave={handleClickCreateReminder}
          createProgramReminderLoading={false}
          disallowSendNow
        />
      )}
      <div className="flex items-center justify-between">
        <div className="text-xl font-semibold">Scheduled Reminders</div>
        {canUpdate && (
          <Button
            text="Schedule a Reminder"
            tooltip={cannotCreateReminder}
            disabled={!!cannotCreateReminder}
            theme={buttonTheme.lightBlue}
            onClick={() => setIsShowingSendReminderDialog(true)}
          />
        )}
      </div>

      <div className="mt-6">
        <TableContainer className="mt-6">
          <Table>
            <TableHeadRow>
              <TableHeadCell roundedLeft widthPx={220}>
                Scheduled Date
              </TableHeadCell>
              <TableHeadCell widthPx={192}>
                {label("review", { titleCase: true })} Steps
              </TableHeadCell>
              <TableHeadCell widthPx={192}>Reminder Type</TableHeadCell>
              <TableHeadCell>Message</TableHeadCell>
              <TableHeadCell widthPx={24}> </TableHeadCell>
            </TableHeadRow>
            <TableBody>
              {scheduledReminders.map((reminder, index) => (
                <TableBodyRow key={`reminder-${reminder.id ?? index}`}>
                  <TableBodyCell className="text-gray-800 w-72">
                    {showRelativeDateInput
                      ? `${reminder.daysAfterStartDate} days after start date`
                      : `${moment(proposedComplianceProgram.startDate)
                          .add(reminder.daysAfterStartDate, "days")
                          .format("MMM D, YYYY")}`}
                  </TableBodyCell>
                  <TableBodyCell>
                    {compact([
                      reminder.nominations && "Peer Nominations",
                      reminder.oneonone &&
                        label("oneonone", { titleCase: true }),
                      reminder.performanceAssessment &&
                        `${getAssessmentTypeLabel(
                          AssessmentType.Performance,
                        )} ${label("review", { titleCase: true })}`,
                      reminder.performanceSelfAssessment &&
                        `${getAssessmentTypeLabel(
                          AssessmentType.Performance,
                          true,
                        )} ${label("review", { titleCase: true })}`,
                      reminder.managerAssessment &&
                        getAssessmentTypeLabel(AssessmentType.Manager),
                      reminder.peerAssessment &&
                        getAssessmentTypeLabel(AssessmentType.Peer),
                      reminder.engagementSurvey &&
                        getAssessmentTypeLabel(AssessmentType.EngagementSurvey),
                    ]).join(", ")}
                  </TableBodyCell>
                  <TableBodyCell>
                    {compact([
                      reminder.emailNotification && "Email",
                      reminder.slackNotification && "Slack",
                      reminder.teamsNotification && "Teams",
                    ]).join(", ")}
                  </TableBodyCell>
                  <TableBodyCell>
                    <Tooltip text={reminder.message}>
                      <span className="truncate inline-block max-w-48">
                        {reminder.message || "N/A"}
                      </span>
                    </Tooltip>
                  </TableBodyCell>
                  <TableBodyCell>
                    {canUpdate && (
                      <Button
                        icon={getTopicflowIcon("trash")}
                        theme={buttonTheme.iconGray}
                        onClick={() => handleDeleteReminder(index, reminder.id)}
                      />
                    )}
                  </TableBodyCell>
                </TableBodyRow>
              ))}
              {scheduledReminders.length === 0 && (
                <TableBodyRow>
                  <TableBodyCell
                    colSpan={5}
                    className="text-center p-4 text-gray-500"
                  >
                    No reminders.
                  </TableBodyCell>
                </TableBodyRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default ComplianceProgramFormReminders;
